import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TimeInput12 from "@/components/TimeInput12";
import RehearsalScheduleField from "@/components/RehearsalScheduleField";
import DateInputMDY from "@/components/DateInputMDY";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Trash2, X, CalendarPlus, AlertTriangle, Printer, GripVertical, Users as UsersIcon, Search } from "lucide-react";
import { fmtDate, fmtTime, fmtTimeRange } from "@/lib/format";
import DatePickerField from "@/components/DatePickerField";
import RehearsalConflictPreview from "@/components/RehearsalConflictPreview";
import RehearsalCheckinQR from "@/components/RehearsalCheckinQR";
import RoomPicker from "@/components/RoomPicker";

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function CalendarPage() {
  const { user } = useAuth();
  const canEdit = user.role === "org_admin" || user.role === "production_staff" || user.role === "super_admin";
  const isCast = user.role === "cast";

  const [view, setView] = useState("month");    // "month" | "week"
  const [rehearsals, setRehearsals] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);       // Iter 74 — for pre-fill on create
  const [familyCalls, setFamilyCalls] = useState([]); // Iter 75 — every call for this login's family
  const [hiddenFamily, setHiddenFamily] = useState({}); // Iter 75 — {user_id: true} = hidden on calendar
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const familyMembers = user.family_members || [];
  const hasFamily = familyMembers.length > 1;

  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ title: "", date: ymd(new Date()), start_time: "19:00", end_time: "22:00", location: "Rehearsal Studio B", space_id: null, notes: "", scene_ids: [] });
  // Iter 74 — optional pre-fill on rehearsal creation. Applied after the
  // rehearsal is inserted via the existing /calls and /character-groups/{id}/call
  // endpoints so we reuse conflict-checking + idempotency for free.
  const [prefillUsers, setPrefillUsers] = useState([]);   // array of user_ids
  const [prefillGroups, setPrefillGroups] = useState([]); // array of group_ids
  const [prefillSearch, setPrefillSearch] = useState(""); // Iter 79 — quick-search names

  const [openConflict, setOpenConflict] = useState(false);
  const [conflictForm, setConflictForm] = useState({ date: ymd(new Date()), start_time: "18:00", end_time: "22:00", reason: "" });

  const load = async () => {
    const [r, s, u] = await Promise.all([
      api.get("/rehearsals"),
      api.get("/scenes"),
      api.get("/users"),
    ]);
    setRehearsals(r.data);
    setScenes(s.data);
    setUsers(u.data);
    if (canEdit) {
      // Only staff/admin can call groups — avoid a 403 for cast.
      try {
        const g = await api.get("/character-groups");
        setGroups(g.data || []);
      } catch { /* silent */ }
    }
    if (hasFamily) {
      try {
        const fc = await api.get("/calls/mine");
        setFamilyCalls(fc.data || []);
      } catch { /* silent */ }
    }
  };
  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const m = {};
    for (const r of rehearsals) (m[r.date] ||= []).push(r);
    // sort each day by start_time
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return m;
  }, [rehearsals]);

  // Iter 75 — map every rehearsal to the set of visible family members
  // who have a call for it, so month/week cards can render coloured dots.
  const familyByRehearsal = useMemo(() => {
    if (!hasFamily) return {};
    const m = {};
    for (const c of familyCalls) {
      if (hiddenFamily[c.user_id]) continue;
      (m[c.rehearsal_id] ||= new Set()).add(c.user_id);
    }
    return m;
  }, [familyCalls, hiddenFamily, hasFamily]);
  const colorOf = (uid) => (familyMembers.find((m) => m.id === uid) || {}).color || "#F59E0B";

  // MONTH grid
  const monthDays = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const firstDay = start.getDay();
    const total = end.getDate();
    const arr = [];
    for (let i = 0; i < firstDay; i++) arr.push(null);
    for (let d = 1; d <= total; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  // WEEK
  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const openNewRehearsalOn = (dateStr) => { setForm((f) => ({ ...f, date: dateStr, title: "" })); setOpenNew(true); };
  const openConflictOn = (dateStr) => { setConflictForm((f) => ({ ...f, date: dateStr, reason: "" })); setOpenConflict(true); };

  const handleDayClick = (d) => {
    if (!d) return;
    const dateStr = ymd(d);
    if (canEdit) openNewRehearsalOn(dateStr);
    else if (isCast) openConflictOn(dateStr);
  };

  const createRehearsal = async (override = false) => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const { data: created } = await api.post("/rehearsals", { ...form, override_conflict: override });
      // Iter 74 — apply pre-fills after the rehearsal exists. Failures on
      // any single call are surfaced but never block the rehearsal itself.
      let indivAdded = 0;
      let groupCallsAdded = 0;
      for (const uid of prefillUsers) {
        try {
          await api.post("/calls", {
            rehearsal_id: created.id,
            user_id: uid,
            call_time: form.start_time,
            role_note: "",
            status: "called",
          });
          indivAdded += 1;
        } catch { /* skip; the rehearsal is already saved */ }
      }
      for (const gid of prefillGroups) {
        try {
          const { data } = await api.post(`/character-groups/${gid}/call`, {
            rehearsal_id: created.id,
            call_time: form.start_time,
            status: "called",
          });
          groupCallsAdded += (data?.calls_created || 0);
        } catch { /* skip */ }
      }
      const extra = indivAdded + groupCallsAdded > 0
        ? ` · ${indivAdded + groupCallsAdded} call${indivAdded + groupCallsAdded === 1 ? "" : "s"} added`
        : "";
      toast.success(`Rehearsal added${extra}`);
      setOpenNew(false);
      setForm({ ...form, title: "", notes: "" });
      setPrefillUsers([]);
      setPrefillGroups([]);
      load();
    } catch (e) {
      // 409 = double-book (room) OR cast-conflict. Show the specifics and
      // let the admin explicitly override.
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        const isCastConflict = detail.kind === "cast_conflict";
        const list = (detail.conflicts || []).map((c) =>
          isCastConflict
            ? `• ${c.user_name} — ${c.start_time || "all day"}${c.end_time ? "–" + c.end_time : ""}${c.reason ? " (" + c.reason + ")" : ""}`
            : `• ${c.title} (${c.start_time}–${c.end_time})`
        ).join("\n");
        if (confirm(`${detail.message || "Conflict detected."}\n\n${list}\n\nSchedule anyway (override)?`)) {
          return createRehearsal(true);
        }
      } else {
        toast.error("Failed to create rehearsal");
      }
    }
  };

  const submitConflict = async () => {
    if (!conflictForm.reason.trim()) return toast.error("Please describe the reason");
    try {
      await api.post("/conflicts", conflictForm);
      toast.success("Conflict submitted");
      setOpenConflict(false);
      setConflictForm({ ...conflictForm, reason: "" });
    } catch { toast.error("Failed to submit conflict"); }
  };

  const deleteRehearsal = async (id) => {
    if (!confirm("Delete this rehearsal and all associated calls?")) return;
    await api.delete(`/rehearsals/${id}`);
    toast.success("Deleted");
    setSelected(null);
    load();
  };

  // ---- DnD reschedule ----
  const onDragStart = (e, r) => {
    if (!canEdit) return;
    e.dataTransfer.setData("text/plain", r.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOverDay = (e, dateStr) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverDate !== dateStr) setDragOverDate(dateStr);
  };
  const onDragLeaveDay = () => setDragOverDate(null);
  const onDropDay = async (e, dateStr) => {
    e.preventDefault();
    setDragOverDate(null);
    const id = e.dataTransfer.getData("text/plain");
    const r = rehearsals.find((x) => x.id === id);
    if (!r || r.date === dateStr) return;
    // optimistic update
    setRehearsals((prev) => prev.map((x) => x.id === id ? { ...x, date: dateStr } : x));
    try {
      await api.patch(`/rehearsals/${id}`, {
        title: r.title, date: dateStr, start_time: r.start_time, end_time: r.end_time,
        location: r.location || "", scene_ids: r.scene_ids || [], notes: r.notes || "", status: r.status || "scheduled",
      });
      toast.success(`Moved "${r.title}" to ${fmtDate(dateStr)}`);
    } catch {
      toast.error("Could not reschedule — reverting");
      load();
    }
  };

  const shift = (dir) => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
    else setCursor(addDays(cursor, 7 * dir));
  };

  // Month header stays "September 2026" (it's a month name, not a date).
  // Week header shows MM-DD-YYYY on both ends for the strict MDY rule.
  const rangeLabel = view === "month"
    ? cursor.toLocaleString("en-US", { month: "long", year: "numeric" })
    : `${fmtDate(weekStart)} – ${fmtDate(addDays(weekStart, 6))}`;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="calendar-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Schedule</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Rehearsal Calendar</h1>
          <p className="mt-1 text-zinc-400 text-sm">
            {canEdit
              ? "Click any day to add a rehearsal. Drag rehearsals between days to reschedule."
              : "Click any day to submit a conflict for that date."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* View switcher */}
          <div className="flex items-center rounded-md border border-white/10 bg-[#121212] p-0.5" role="tablist">
            <button data-testid="view-month" onClick={() => setView("month")}
              className={`h-8 px-3 text-xs uppercase tracking-widest rounded ${view === "month" ? "bg-gold text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>Month</button>
            <button data-testid="view-week" onClick={() => setView("week")}
              className={`h-8 px-3 text-xs uppercase tracking-widest rounded ${view === "week" ? "bg-gold text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>Week</button>
          </div>
          <div className="flex items-center rounded-md border border-white/10 bg-[#121212]">
            <button data-testid="cal-prev" onClick={() => shift(-1)} className="p-2 hover:bg-white/5"><ChevronLeft className="w-4 h-4" /></button>
            <div className="px-3 py-1.5 font-display text-lg min-w-[220px] text-center">{rangeLabel}</div>
            <button data-testid="cal-next" onClick={() => shift(1)} className="p-2 hover:bg-white/5"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 text-sm">Today</button>
          {canEdit && (
            <button data-testid="add-rehearsal-btn" onClick={() => openNewRehearsalOn(ymd(new Date()))} style={goldBtn}
              className="h-9 px-4 rounded-md font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110 transition-all shadow-[0_0_0_1px_rgba(245,158,11,0.4),0_8px_20px_-8px_rgba(245,158,11,0.5)]">
              <CalendarPlus className="w-4 h-4" /> Add rehearsal
            </button>
          )}
          {isCast && (
            <button data-testid="add-conflict-btn" onClick={() => openConflictOn(ymd(new Date()))} style={goldBtn}
              className="h-9 px-4 rounded-md font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110">
              <AlertTriangle className="w-4 h-4" /> Submit conflict
            </button>
          )}
        </div>
      </header>

      {/* Iter 75 — Family filter row. Only surfaces when this login owns
          multiple performers (parent with kids). Each chip toggles that
          performer's calls on/off across the calendar. Colours match the
          dots painted on rehearsal cards below. */}
      {hasFamily && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-[#0F0F0F] p-2.5" data-testid="family-filter-bar">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 pl-1 pr-2">Family</div>
          {familyMembers.map((m) => {
            const hidden = !!hiddenFamily[m.id];
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`family-toggle-${m.id}`}
                onClick={() => setHiddenFamily((cur) => ({ ...cur, [m.id]: !cur[m.id] }))}
                className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1.5 transition-opacity ${hidden ? "border-white/10 text-zinc-500 opacity-50" : "border-white/20 text-zinc-100"}`}
                title={hidden ? "Show" : "Hide"}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                {m.name}
              </button>
            );
          })}
          <div className="text-[10px] text-zinc-500 ml-auto pr-1">Tap a name to hide their calls. Coloured dots on each rehearsal show who&apos;s called.</div>
        </div>
      )}

      {/* MONTH VIEW */}
      {view === "month" && (
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] overflow-hidden">
          <div className="grid grid-cols-7 border-b border-white/10">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map((d, i) => {
              const key = d ? ymd(d) : `empty-${i}`;
              const events = d ? (byDate[ymd(d)] || []) : [];
              const isToday = d && ymd(d) === ymd(new Date());
              const dateStr = d ? ymd(d) : null;
              const dropActive = dragOverDate === dateStr;
              return (
                <div
                  key={key}
                  onDragOver={(e) => d && onDragOverDay(e, dateStr)}
                  onDragLeave={onDragLeaveDay}
                  onDrop={(e) => d && onDropDay(e, dateStr)}
                  className={`group relative min-h-[112px] border-b border-r border-white/5 p-2 transition-colors
                    ${!d ? "bg-[#0A0A0A]" : "hover:bg-white/[0.03]"} 
                    ${dropActive ? "!bg-gold/10 ring-1 ring-gold" : ""}`}
                >
                  {d && (
                    <>
                      <div className="flex items-center justify-between">
                        <div className={`text-xs ${isToday ? "text-gold font-semibold" : "text-zinc-500"}`}>{d.getDate()}</div>
                        {(canEdit || isCast) && (
                          <button data-testid={`day-add-${dateStr}`} onClick={() => handleDayClick(d)}
                            title={canEdit ? "Add rehearsal" : "Submit conflict"}
                            className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 rounded grid place-items-center border border-gold/40 text-gold hover:bg-gold/10">
                            <Plus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="mt-1 space-y-1">
                        {events.slice(0, 3).map((r) => (
                          <div
                            key={r.id}
                            draggable={canEdit}
                            onDragStart={(e) => onDragStart(e, r)}
                            className={`flex items-center gap-1 w-full text-[11px] rounded px-2 py-1 bg-gold/10 hover:bg-gold/20 text-gold border border-gold/20 truncate ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
                          >
                            {canEdit && <GripVertical className="w-3 h-3 opacity-40 shrink-0" />}
                            <button onClick={() => setSelected(r)} data-testid={`event-${r.id}`} className="flex-1 text-left truncate">
                              {fmtTime(r.start_time)} · {r.title}
                            </button>
                            {hasFamily && (familyByRehearsal[r.id]?.size > 0) && (
                              <span className="flex items-center gap-0.5 shrink-0" data-testid={`event-family-dots-${r.id}`}>
                                {[...familyByRehearsal[r.id]].slice(0, 4).map((uid) => (
                                  <span key={uid} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorOf(uid) }} title={(familyMembers.find((m) => m.id === uid) || {}).name} />
                                ))}
                              </span>
                            )}
                          </div>
                        ))}
                        {events.length > 3 && <div className="text-[10px] text-zinc-500 px-2">+{events.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WEEK VIEW */}
      {view === "week" && (
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] overflow-hidden">
          <div className="grid grid-cols-7 border-b border-white/10">
            {weekDays.map((d) => {
              const isToday = ymd(d) === ymd(new Date());
              return (
                <div key={ymd(d)} className={`px-3 py-2 border-r border-white/5 last:border-r-0 ${isToday ? "bg-gold/5" : ""}`}>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                  <div className={`text-lg font-display ${isToday ? "text-gold" : "text-white"}`}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 min-h-[540px]">
            {weekDays.map((d) => {
              const dateStr = ymd(d);
              const events = byDate[dateStr] || [];
              const dropActive = dragOverDate === dateStr;
              return (
                <div
                  key={dateStr}
                  data-testid={`week-day-${dateStr}`}
                  onDragOver={(e) => onDragOverDay(e, dateStr)}
                  onDragLeave={onDragLeaveDay}
                  onDrop={(e) => onDropDay(e, dateStr)}
                  className={`group relative border-r border-white/5 last:border-r-0 p-2 transition-colors ${dropActive ? "bg-gold/10 ring-1 ring-gold" : "hover:bg-white/[0.02]"}`}
                >
                  {(canEdit || isCast) && (
                    <button
                      data-testid={`week-add-${dateStr}`}
                      onClick={() => handleDayClick(d)}
                      title={canEdit ? "Add rehearsal" : "Submit conflict"}
                      className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 w-5 h-5 rounded grid place-items-center border border-gold/40 text-gold hover:bg-gold/10 transition-opacity"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                  <div className="space-y-1.5">
                    {events.map((r) => (
                      <div
                        key={r.id}
                        draggable={canEdit}
                        onDragStart={(e) => onDragStart(e, r)}
                        onClick={() => setSelected(r)}
                        data-testid={`week-event-${r.id}`}
                        className={`rounded-md border border-gold/25 bg-gold/10 hover:bg-gold/20 text-gold p-2 text-[11px] ${canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center gap-1">
                          {canEdit && <GripVertical className="w-3 h-3 opacity-40 shrink-0" />}
                          <span className="font-mono">{fmtTime(r.start_time)}–{fmtTime(r.end_time)}</span>
                        </div>
                        <div className="mt-0.5 text-white font-medium leading-tight truncate">{r.title}</div>
                        {r.location && <div className="text-[10px] text-zinc-400 truncate">{r.location}</div>}
                      </div>
                    ))}
                    {events.length === 0 && (
                      <div className="text-[10px] uppercase tracking-widest text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity mt-2">Drop here</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {canEdit && (
        <p className="text-xs text-zinc-500">
          Tip: hover any date to reveal a quick <span className="text-gold">＋</span>. Drag any rehearsal to a different day to reschedule.
        </p>
      )}

      {/* New rehearsal dialog */}
      <Dialog open={openNew} onOpenChange={(v) => { setOpenNew(v); if (!v) { setPrefillUsers([]); setPrefillGroups([]); setPrefillSearch(""); } }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Add rehearsal</DialogTitle>
            <DialogDescription className="text-zinc-500">Schedule a new rehearsal and add calls afterwards.</DialogDescription>
          </DialogHeader>

          {/* Iter 79 — cast conflict banner spans the full width above the
              two columns since it's the highest-priority attention grabber
              when a scheduler is about to double-book a performer. */}
          {canEdit && form.date && (
            <RehearsalConflictPreview date={form.date} startTime={form.start_time} endTime={form.end_time} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-[minmax(320px,1fr)_minmax(340px,1.2fr)] gap-6">
            {/* Left column — Basics */}
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Basics</div>
              <div><Label>Title</Label><Input data-testid="rehearsal-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div>
                <Label>When</Label>
                <RehearsalScheduleField
                  date={form.date}
                  startTime={form.start_time}
                  endTime={form.end_time}
                  onChange={({ date, startTime, endTime }) => setForm({ ...form, date, start_time: startTime, end_time: endTime })}
                  testid="rehearsal-schedule"
                />
              </div>
              <div>
                <Label>Room (facility)</Label>
                <RoomPicker
                  value={form.space_id}
                  onChange={(v) => setForm({ ...form, space_id: v })}
                  date={form.date}
                  startTime={form.start_time}
                  endTime={form.end_time}
                />
              </div>
              <div><Label>Location note (free text)</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="e.g. bring water" /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10" rows={4} /></div>
            </div>

            {/* Right column — Call people (Iter 74) */}
            {canEdit ? (
              <div className="rounded-lg border border-gold/20 bg-gold/5 p-4 space-y-4 self-start" data-testid="rehearsal-prefill">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-gold inline-flex items-center gap-1.5"><UsersIcon className="w-3 h-3" /> Call people to this rehearsal</div>
                  {(prefillUsers.length + prefillGroups.length) > 0 && (
                    <span data-testid="prefill-count" className="text-[10px] uppercase tracking-widest text-gold">{prefillUsers.length + prefillGroups.length} selected</span>
                  )}
                </div>

                {/* Individual multi-select */}
                <div>
                  <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">People</div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        data-testid="prefill-select-all-cast"
                        onClick={() => {
                          const q = prefillSearch.trim().toLowerCase();
                          const castIds = users
                            .filter((u) => u.role === "cast")
                            .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.character || "").toLowerCase().includes(q))
                            .map((u) => u.id);
                          setPrefillUsers((cur) => Array.from(new Set([...cur, ...castIds])));
                        }}
                        className="text-[10px] uppercase tracking-widest text-gold border border-gold/40 rounded px-2 py-0.5 hover:bg-gold/10"
                      >
                        All cast
                      </button>
                      <button
                        type="button"
                        data-testid="prefill-select-all-staff"
                        onClick={() => {
                          const q = prefillSearch.trim().toLowerCase();
                          const ids = users
                            .filter((u) => u.role === "production_staff" || u.role === "support_staff")
                            .filter((u) => !q || (u.name || "").toLowerCase().includes(q))
                            .map((u) => u.id);
                          setPrefillUsers((cur) => Array.from(new Set([...cur, ...ids])));
                        }}
                        className="text-[10px] uppercase tracking-widest text-sky-300 border border-sky-400/40 rounded px-2 py-0.5 hover:bg-sky-400/10"
                      >
                        All staff
                      </button>
                      <button
                        type="button"
                        data-testid="prefill-select-all-admins"
                        onClick={() => {
                          const q = prefillSearch.trim().toLowerCase();
                          const ids = users
                            .filter((u) => u.role === "org_admin" || u.role === "producer")
                            .filter((u) => !q || (u.name || "").toLowerCase().includes(q))
                            .map((u) => u.id);
                          setPrefillUsers((cur) => Array.from(new Set([...cur, ...ids])));
                        }}
                        className="text-[10px] uppercase tracking-widest text-violet-300 border border-violet-400/40 rounded px-2 py-0.5 hover:bg-violet-400/10"
                      >
                        All admins
                      </button>
                      <button
                        type="button"
                        data-testid="prefill-select-all"
                        onClick={() => {
                          const q = prefillSearch.trim().toLowerCase();
                          const ids = users
                            .filter((u) => u.role !== "super_admin")
                            .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.character || "").toLowerCase().includes(q))
                            .map((u) => u.id);
                          setPrefillUsers((cur) => Array.from(new Set([...cur, ...ids])));
                        }}
                        className="text-[10px] uppercase tracking-widest text-zinc-300 border border-white/15 rounded px-2 py-0.5 hover:bg-white/5"
                      >
                        All
                      </button>
                      <button
                        type="button"
                        data-testid="prefill-clear"
                        onClick={() => setPrefillUsers([])}
                        className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="relative flex-1 max-w-[220px]">
                      <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <input
                        data-testid="prefill-search-input"
                        value={prefillSearch}
                        onChange={(e) => setPrefillSearch(e.target.value)}
                        placeholder="Search name…"
                        className="w-full h-7 bg-[#0A0A0A] border border-white/10 rounded pl-7 pr-2 text-xs text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-gold/40"
                      />
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto rounded-md border border-white/10 bg-[#0A0A0A] divide-y divide-white/5" data-testid="prefill-users-list">
                    {(() => {
                      const q = prefillSearch.trim().toLowerCase();
                      // Iter 80 — show every production member we can call, not
                      // just cast. Production staff / producers / directors can
                      // all legitimately be summoned to a rehearsal. super_admin
                      // is excluded because they are a platform role, not a
                      // production member.
                      const filtered = users
                        .filter((u) => u.role !== "super_admin")
                        .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.character || "").toLowerCase().includes(q));
                      if (filtered.length === 0) {
                        return <div className="p-3 text-xs text-zinc-500">{q ? "No matches." : "No people on this production yet."}</div>;
                      }
                      // Role chip colour — cast is gold, everyone else is zinc so
                      // staff stand out visually from performers.
                      const roleChip = (role) => {
                        if (role === "cast") return "border-gold/30 bg-gold/5 text-gold";
                        return "border-sky-400/30 bg-sky-400/5 text-sky-300";
                      };
                      const roleLabel = (role) => ({
                        cast: "cast",
                        production_staff: "staff",
                        support_staff: "support",
                        producer: "producer",
                        org_admin: "admin",
                      }[role] || role);
                      return filtered.map((u) => {
                        const active = prefillUsers.includes(u.id);
                        return (
                          <button
                            key={u.id}
                            type="button"
                            data-testid={`prefill-user-${u.id}`}
                            onClick={() =>
                              setPrefillUsers((cur) =>
                                cur.includes(u.id) ? cur.filter((x) => x !== u.id) : [...cur, u.id]
                              )
                            }
                            className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 hover:bg-white/5 ${active ? "bg-gold/10 text-gold" : "text-zinc-200"}`}
                          >
                            <span className={`w-3.5 h-3.5 rounded border shrink-0 inline-flex items-center justify-center ${active ? "bg-gold border-gold" : "border-white/25"}`}>
                              {active && <span className="text-black text-[9px] leading-none">✓</span>}
                            </span>
                            <span className="truncate flex-1">{u.name}</span>
                            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${roleChip(u.role)}`}>{roleLabel(u.role)}</span>
                            {u.character && <span className="text-[10px] text-zinc-500 truncate max-w-[7rem]">{u.character}</span>}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Group multi-select */}
                {groups.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Ensemble groups</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="prefill-groups-list">
                      {groups.map((g) => {
                        const active = prefillGroups.includes(g.id);
                        return (
                          <button
                            key={g.id}
                            type="button"
                            data-testid={`prefill-group-${g.id}`}
                            onClick={() =>
                              setPrefillGroups((cur) =>
                                cur.includes(g.id) ? cur.filter((x) => x !== g.id) : [...cur, g.id]
                              )
                            }
                            className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                              active ? "bg-gold/20 border-gold/60 text-gold" : "border-white/15 text-zinc-300 hover:bg-white/5"
                            }`}
                          >
                            {g.parent_group_id ? "↳ " : ""}{g.name}
                            <span className="text-[9px] text-zinc-500">{g.total_user_count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-zinc-500">Everyone selected here gets a call at the start time. You can add or remove more from the rehearsal detail after saving.</div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <button data-testid="save-rehearsal-btn" onClick={() => createRehearsal(false)} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save rehearsal</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cast conflict dialog */}
      <Dialog open={openConflict} onOpenChange={setOpenConflict}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Submit conflict</DialogTitle>
            <DialogDescription className="text-zinc-500">Let production know you&apos;re not available.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Date</Label><DatePickerField value={conflictForm.date} onChange={(v) => setConflictForm({ ...conflictForm, date: v })} placeholder="Pick date" /></div>
            <div><Label>Start</Label><TimeInput12 value={conflictForm.start_time} onChange={(v) => setConflictForm({ ...conflictForm, start_time: v })} /></div>
            <div><Label>End</Label><TimeInput12 value={conflictForm.end_time} onChange={(v) => setConflictForm({ ...conflictForm, end_time: v })} /></div>
          </div>
          <div><Label>Reason</Label><Textarea data-testid="cal-conflict-reason-input" value={conflictForm.reason} onChange={(e) => setConflictForm({ ...conflictForm, reason: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <DialogFooter>
            <button data-testid="cal-submit-conflict-btn" onClick={submitConflict} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Submit conflict</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      {selected && (
        <RehearsalDetail rehearsal={selected} onClose={() => setSelected(null)}
          onDelete={canEdit ? () => deleteRehearsal(selected.id) : null}
          scenes={scenes} users={users} canEdit={canEdit} reload={load} />
      )}
    </div>
  );
}

function RehearsalDetail({ rehearsal, onClose, onDelete, scenes, users, canEdit, reload }) {
  const [detail, setDetail] = useState(null);
  const [callForm, setCallForm] = useState({ user_id: "", call_time: rehearsal.start_time, role_note: "", status: "called" });
  const [groups, setGroups] = useState([]);
  const [groupForm, setGroupForm] = useState({ group_id: "", call_time: rehearsal.start_time });
  useEffect(() => {
    api.get(`/rehearsals/${rehearsal.id}`).then((r) => setDetail(r.data));
    if (canEdit) api.get("/character-groups").then((r) => setGroups(r.data || [])).catch(() => {});
  }, [rehearsal.id, canEdit]);

  const addCall = async () => {
    if (!callForm.user_id) return toast.error("Choose a user");
    await api.post("/calls", { ...callForm, rehearsal_id: rehearsal.id });
    toast.success("Call added");
    const r = await api.get(`/rehearsals/${rehearsal.id}`);
    setDetail(r.data);
    reload();
  };
  // Iter 73 — bulk call by group. Cascades through sub-groups server-side
  // and skips users who already have a call for this rehearsal so
  // clicking again is idempotent (no duplicate calls).
  const callGroup = async () => {
    if (!groupForm.group_id) return toast.error("Pick a group");
    try {
      const { data } = await api.post(`/character-groups/${groupForm.group_id}/call`, {
        rehearsal_id: rehearsal.id,
        call_time: groupForm.call_time,
        status: "called",
      });
      toast.success(`${data.calls_created} call${data.calls_created === 1 ? "" : "s"} added${data.already_called ? ` · ${data.already_called} already called` : ""}`);
      const r = await api.get(`/rehearsals/${rehearsal.id}`);
      setDetail(r.data);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't call group"); }
  };
  const removeCall = async (id) => {
    await api.delete(`/calls/${id}`);
    const r = await api.get(`/rehearsals/${rehearsal.id}`);
    setDetail(r.data);
    reload();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between">
            <span>{rehearsal.title}</span>
            <div className="flex items-center gap-2">
              {canEdit && (
                <RehearsalCheckinQR rehearsalId={rehearsal.id} rehearsalTitle={rehearsal.title} />
              )}
              <Link
                to={`/call-sheet/${rehearsal.id}`}
                target="_blank"
                rel="noreferrer"
                data-testid="open-call-sheet-btn"
                className="h-8 px-2.5 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs inline-flex items-center gap-1.5"
              >
                <Printer className="w-3.5 h-3.5" /> Call sheet
              </Link>
              {onDelete && (
                <button data-testid="delete-rehearsal-btn" onClick={onDelete} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </DialogTitle>
          <DialogDescription className="text-zinc-500">Rehearsal details and calls.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-sm text-zinc-300">
            <div className="rounded border border-white/10 p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Date</div>{fmtDate(rehearsal.date)}</div>
            <div className="rounded border border-white/10 p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Time</div>{fmtTimeRange(rehearsal.start_time, rehearsal.end_time)}</div>
            <div className="rounded border border-white/10 p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Location</div>{rehearsal.location}</div>
          </div>
          {canEdit && (
            <RehearsalConflictPreview date={rehearsal.date} startTime={rehearsal.start_time} endTime={rehearsal.end_time} />
          )}
          {rehearsal.notes && <div className="text-sm text-zinc-400">{rehearsal.notes}</div>}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Calls</div>
            <div className="rounded border border-white/10 divide-y divide-white/5">
              {(detail?.calls || []).length === 0 && <div className="p-3 text-sm text-zinc-500">No calls yet.</div>}
              {(detail?.calls || []).map((c) => {
                const u = users.find((x) => x.id === c.user_id);
                const roles = (u?.character || "").split(" / ").filter(Boolean);
                return (
                  <div key={c.id} className="p-3 flex items-center gap-3 text-sm">
                    <span className="w-20 text-gold font-mono">{fmtTime(c.call_time)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-1.5">
                        <span>{u?.name || "Unknown"}</span>
                        {roles.length > 0 ? (
                          roles.map((r) => (
                            <span key={r} className="text-[10px] px-1.5 py-0.5 rounded border border-gold/30 bg-gold/5 text-gold uppercase tracking-widest">{r}</span>
                          ))
                        ) : (
                          <span className="text-zinc-500 text-xs">· {u?.role}</span>
                        )}
                      </div>
                      {c.role_note && <div className="text-xs text-zinc-500 truncate mt-0.5">{c.role_note}</div>}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-emerald-400">{c.status}</span>
                    {canEdit && <button onClick={() => removeCall(c.id)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>}
                  </div>
                );
              })}
            </div>
          </div>

          {canEdit && groups.length > 0 && (
            <div className="rounded-lg border border-gold/25 bg-gold/5 p-3" data-testid="call-group-panel">
              <div className="text-[10px] uppercase tracking-widest text-gold mb-2 inline-flex items-center gap-1.5"><UsersIcon className="w-3 h-3" /> Call an ensemble / group</div>
              <div className="grid grid-cols-4 gap-2">
                <Select value={groupForm.group_id} onValueChange={(v) => setGroupForm({ ...groupForm, group_id: v })}>
                  <SelectTrigger data-testid="call-group-select" className="bg-[#0A0A0A] border-white/10 col-span-2"><SelectValue placeholder="Choose group" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id} data-testid={`call-group-opt-${g.id}`}>
                        {g.parent_group_id ? "  ↳ " : ""}{g.name} · {g.total_user_count} member{g.total_user_count === 1 ? "" : "s"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TimeInput12 value={groupForm.call_time} onChange={(v) => setGroupForm({ ...groupForm, call_time: v })} />
                <button data-testid="call-group-btn" onClick={callGroup} style={goldBtn} className="h-9 px-3 rounded-md font-semibold text-sm hover:brightness-110">Call group</button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">Calling a parent group also calls everyone in every sub-group. Existing calls stay untouched.</div>
            </div>
          )}

          {canEdit && (
            <div className="rounded-lg border border-white/10 p-3 bg-[#0F0F0F]">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Add individual call</div>
              <div className="grid grid-cols-4 gap-2">
                <Select value={callForm.user_id} onValueChange={(v) => setCallForm({ ...callForm, user_id: v })}>
                  <SelectTrigger data-testid="call-user-select" className="bg-[#0A0A0A] border-white/10 col-span-2"><SelectValue placeholder="Choose user" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} — {u.character || u.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TimeInput12 value={callForm.call_time} onChange={(v) => setCallForm({ ...callForm, call_time: v })} />
                <Select value={callForm.status} onValueChange={(v) => setCallForm({ ...callForm, status: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="called">Called</SelectItem>
                    <SelectItem value="hold">Hold</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input value={callForm.role_note} onChange={(e) => setCallForm({ ...callForm, role_note: e.target.value })} placeholder="Role / notes" className="mt-2 bg-[#0A0A0A] border-white/10" />
              <div className="mt-2 flex justify-end">
                <button data-testid="add-call-btn" onClick={addCall} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Add call</button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
